import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiCheck, FiChevronDown, FiLock } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import siteConfig, { formatPrice } from "../config/siteConfig";
import "../css/checkout.css";

const initialForm = {
  email: "",
  country: "United States",
  firstName: "",
  lastName: "",
  company: "",
  address: "",
  apartment: "",
  city: "",
  state: "",
  postalCode: "",
  phone: "",
  cardNumber: "",
  expiry: "",
  cvc: "",
  cardName: "",
  billingCountry: "United States",
  billingFirstName: "",
  billingLastName: "",
  billingAddress: "",
  billingCity: "",
  billingState: "",
  billingPostalCode: "",
};

function CheckOut() {
  const { cartItems, clearCart, appliedDiscountCode } = useContext(CartContext);
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [billingSame, setBillingSame] = useState(true);
  const discountApplied = appliedDiscountCode === siteConfig.commerce.discountCode;

  const detailedItems = useMemo(() => cartItems.map((item) => ({ ...item, product: productData.find((product) => product.id === item.id) })).filter((item) => item.product), [cartItems]);
  const subtotal = detailedItems.reduce((total, item) => total + item.product.price * item.quantity, 0);
  const discount = discountApplied ? subtotal * siteConfig.commerce.discountRate : 0;
  const shipping = subtotal - discount >= siteConfig.commerce.shippingThreshold ? 0 : siteConfig.commerce.standardShipping;
  const total = subtotal - discount + shipping;

  const setField = (name, value) => {
    setForm((current) => ({ ...current, [name]: value }));
    if (errors[name]) setErrors((current) => ({ ...current, [name]: "" }));
  };

  const handleInput = (event) => {
    const { name, value } = event.target;
    if (name === "cardNumber") {
      const digits = value.replace(/\D/g, "").slice(0, 16);
      setField(name, digits.replace(/(\d{4})(?=\d)/g, "$1 "));
      return;
    }
    if (name === "expiry") {
      const digits = value.replace(/\D/g, "").slice(0, 4);
      setField(name, digits.length > 2 ? `${digits.slice(0, 2)} / ${digits.slice(2)}` : digits);
      return;
    }
    if (name === "cvc") {
      setField(name, value.replace(/\D/g, "").slice(0, 4));
      return;
    }
    setField(name, value);
  };

  const validate = () => {
    const nextErrors = {};
    ["email", "firstName", "lastName", "address", "city", "state", "postalCode", "cardName"].forEach((field) => {
      if (!form[field].trim()) nextErrors[field] = "This field is required";
    });
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = "Enter a valid email address";
    if (form.cardNumber.replace(/\s/g, "").length !== 16) nextErrors.cardNumber = "Enter a 16-digit card number";
    if (!/^\d{2} \/ \d{2}$/.test(form.expiry)) nextErrors.expiry = "Use MM / YY";
    if (form.cvc.length < 3) nextErrors.cvc = "Enter your security code";
    if (!billingSame) {
      ["billingFirstName", "billingLastName", "billingAddress", "billingCity", "billingState", "billingPostalCode"].forEach((field) => {
        if (!form[field].trim()) nextErrors[field] = "This field is required";
      });
    }
    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      const firstField = document.querySelector(`[name="${Object.keys(nextErrors)[0]}"]`);
      firstField?.focus();
      return;
    }

    setSubmitting(true);
    await new Promise((resolve) => window.setTimeout(resolve, 650));
    const orderNumber = `${siteConfig.commerce.orderPrefix}-${Date.now().toString().slice(-8)}`;
    clearCart();
    navigate("/paysuccess", { replace: true, state: { orderNumber, email: form.email, total } });
  };

  const field = (name, label, options = {}) => (
    <label className={`checkout-field ${errors[name] ? "has-error" : ""}`}>
      <span>{label}{options.required && " *"}</span>
      <input name={name} type={options.type || "text"} autoComplete={options.autoComplete} inputMode={options.inputMode} value={form[name]} onChange={handleInput} placeholder={options.placeholder || ""} required={options.required} aria-required={options.required || undefined} aria-invalid={Boolean(errors[name])} aria-describedby={errors[name] ? `${name}-error` : undefined} />
      {errors[name] && <small id={`${name}-error`} role="alert">{errors[name]}</small>}
    </label>
  );

  const orderSummary = (
    <div className="checkout-summary__content">
      <div className="checkout-products">
        {detailedItems.map((item) => <article key={`${item.id}-${item.size}-${item.color}`}><div><img src={item.product.image} alt={`${item.product.name} — ${item.product.colorName}`} /><span>{item.quantity}</span></div><p><strong>{item.product.name}</strong><small>{item.product.colorName} · {item.size}</small></p><b>{formatPrice(item.product.price * item.quantity)}</b></article>)}
      </div>
      <div className="checkout-summary__rows"><p><span>Subtotal</span><b>{formatPrice(subtotal)}</b></p>{discountApplied && <p className="checkout-summary__discount"><span>{appliedDiscountCode}</span><b>−{formatPrice(discount)}</b></p>}<p><span>Shipping</span><b>{shipping === 0 ? "Free" : formatPrice(shipping)}</b></p></div>
      <div className="checkout-summary__total"><span>Total <small>{siteConfig.commerce.currency}</small></span><b>{formatPrice(total)}</b></div>
    </div>
  );

  if (detailedItems.length === 0) {
    return <main className="checkout-empty"><span>{siteConfig.logo.symbol}</span><h1>Your checkout is empty.</h1><p>Add a piece to your bag before continuing.</p><Link className="button-primary" to="/shop">Return to shop</Link></main>;
  }

  return (
    <main className="checkout-page">
      <div className="checkout-mobile-summary"><details><summary><span>Order summary <FiChevronDown /></span><strong>{formatPrice(total)}</strong></summary>{orderSummary}</details></div>
      <div className="checkout-layout">
        <section className="checkout-form-wrap">
          <div className="checkout-intro"><p className="eyebrow">Secure checkout</p><h1>Almost yours.</h1><p>Complete your details below. This demo store does not process or store real payment information.</p></div>
          <form className="checkout-form" onSubmit={handleSubmit} noValidate>
            <section>
              <div className="checkout-section-title"><h2>Contact</h2><span>1 / 3</span></div>
              {field("email", "Email address", { required: true, type: "email", autoComplete: "email", placeholder: "you@example.com" })}
              <label className="checkout-checkbox"><input type="checkbox" checked={marketing} onChange={(event) => setMarketing(event.target.checked)} /> <span>Send me thoughtful updates and new releases.</span></label>
            </section>

            <section>
              <div className="checkout-section-title"><h2>Delivery</h2><span>2 / 3</span></div>
              <label className="checkout-field"><span>Country / region *</span><select name="country" value={form.country} onChange={handleInput} required aria-required="true"><option>United States</option><option>Canada</option><option>United Kingdom</option><option>Spain</option><option>France</option><option>Germany</option></select></label>
              <div className="checkout-two-fields">{field("firstName", "First name", { required: true, autoComplete: "given-name" })}{field("lastName", "Last name", { required: true, autoComplete: "family-name" })}</div>
              {field("company", "Company (optional)", { autoComplete: "organization" })}
              {field("address", "Address", { required: true, autoComplete: "street-address" })}
              {field("apartment", "Apartment, suite, etc. (optional)", { autoComplete: "address-line2" })}
              <div className="checkout-three-fields">{field("city", "City", { required: true, autoComplete: "address-level2" })}{field("state", "State", { required: true, autoComplete: "address-level1" })}{field("postalCode", "ZIP code", { required: true, autoComplete: "postal-code" })}</div>
              {field("phone", "Phone (optional)", { type: "tel", autoComplete: "tel" })}
              <div className="checkout-shipping-method"><span>Shipping method</span><label><input type="radio" checked readOnly name="shipping" /><span><strong>Tracked standard delivery</strong><small>3–10 working days</small></span><b>{shipping === 0 ? "Free" : formatPrice(shipping)}</b></label></div>
            </section>

            <section>
              <div className="checkout-section-title"><h2>Payment</h2><span>3 / 3</span></div>
              <p className="checkout-secure-copy"><FiLock /> All transactions are encrypted. Use test-style details only.</p>
              <div className="checkout-payment">
                <div className="checkout-payment__head"><span><i /> Credit card</span><div><b>VISA</b><b>MC</b><b>AMEX</b></div></div>
                <div className="checkout-payment__fields">{field("cardNumber", "Card number", { required: true, autoComplete: "cc-number", inputMode: "numeric", placeholder: "0000 0000 0000 0000" })}<div className="checkout-two-fields">{field("expiry", "Expiration date", { required: true, autoComplete: "cc-exp", inputMode: "numeric", placeholder: "MM / YY" })}{field("cvc", "Security code", { required: true, autoComplete: "cc-csc", inputMode: "numeric", placeholder: "CVC" })}</div>{field("cardName", "Name on card", { required: true, autoComplete: "cc-name" })}</div>
              </div>
              <label className="checkout-checkbox checkout-checkbox--billing"><input type="checkbox" checked={billingSame} onChange={(event) => setBillingSame(event.target.checked)} /> <span>Use shipping address as billing address</span></label>
              {!billingSame && (
                <div className="checkout-billing" aria-label="Billing address">
                  <h3>Billing address</h3>
                  <label className="checkout-field"><span>Country / region *</span><select name="billingCountry" value={form.billingCountry} onChange={handleInput} required aria-required="true"><option>United States</option><option>Canada</option><option>United Kingdom</option><option>Spain</option><option>France</option><option>Germany</option></select></label>
                  <div className="checkout-two-fields">{field("billingFirstName", "First name", { required: true, autoComplete: "billing given-name" })}{field("billingLastName", "Last name", { required: true, autoComplete: "billing family-name" })}</div>
                  {field("billingAddress", "Address", { required: true, autoComplete: "billing street-address" })}
                  <div className="checkout-three-fields">{field("billingCity", "City", { required: true, autoComplete: "billing address-level2" })}{field("billingState", "State", { required: true, autoComplete: "billing address-level1" })}{field("billingPostalCode", "ZIP code", { required: true, autoComplete: "billing postal-code" })}</div>
                </div>
              )}
            </section>

            <button className="checkout-submit button-primary" type="submit" disabled={submitting}>{submitting ? <><span className="checkout-spinner" /> Placing order…</> : <><FiLock /> Pay {formatPrice(total)}</>}</button>
            <p className="checkout-terms">By placing your order, you agree to our <Link to="/terms">Terms</Link> and <Link to="/privacy">Privacy Policy</Link>.</p>
          </form>
        </section>

        <aside className="checkout-summary"><div className="checkout-summary__sticky"><p className="eyebrow">Your order</p><h2>{detailedItems.reduce((count, item) => count + item.quantity, 0)} pieces</h2>{orderSummary}<div className="checkout-summary__assurance"><FiCheck /><p><strong>{siteConfig.commerce.returnWindow}-day returns</strong><span>Try your pieces at home.</span></p></div></div></aside>
      </div>
    </main>
  );
}

export default CheckOut;
